import React, { useState, useRef, useEffect } from "react";
import EventCreationForm from "../EventCreationForm/EventCreationForm";
import ScheduleList from "../ScheduleList/ScheduleList";
import "../../styles/components/gathering-customization-form-component.css"; // Import updated CSS for full-height sidebar
import { validateCsvData } from "../../logic/csvScheduleValidation";
import Modal from "../Modal/Modal";
import * as XLSX from "xlsx";
import SpreadsheetEditor from "../../components/SpreadsheetEditor/SpreadsheetEditor";
import ModifyEvent from "../../api/ModifyEvent";

import { doc, getDoc } from "firebase/firestore";
import { firestore } from "../../config/FirebaseConfig";

// Import our CSV validation function

const ScheduleTab = ({
  schedule,
  handleAddEventToSchedule,
  handleDeleteEvent,
  startDate,
  endDate,
  hostID,
  saveEventDetails, // Receive from GCF
  gatheringName,
  gatheringID,
}) => {
  const [isFormExpanded, setIsFormExpanded] = useState(false); // State to track form expansion
  const fileInputRef = useRef(null); // Reference to the hidden file input

  // csv state variables:
  const [isLoading, setIsLoading] = useState(false);
  const [parsedEvents, setParsedEvents] = useState([]);
  const [csvImportErrors, setCsvImportErrors] = useState("");
  const [isImportPreviewOpen, setIsImportPreviewOpen] = useState(false);
  const [isSpreadsheetOpen, setIsSpreadsheetOpen] = useState(false);
  const [importResults, setImportResults] = useState(null);
  const [failedEvents, setFailedEvents] = useState([]); // [{event, errors}]
  const [editingFailedEvent, setEditingFailedEvent] = useState(null);

  const [locationSortMethod, setLocationSortMethod] = useState("manual");
  const [locationOrder, setLocationOrder] = useState(
    Array.from(new Set(schedule.map((e) => e.location))),
  );

  useEffect(() => {
    if (!gatheringID) return;
    const fetchSettings = async () => {
      const ref = doc(firestore, "Gatherings", gatheringID);
      const snap = await getDoc(ref);
      if (snap.exists()) {
        const data = snap.data();
        if (data.locationSortMethod) {
          setLocationSortMethod(data.locationSortMethod);
        }
        if (Array.isArray(data.locationOrder)) {
          setLocationOrder(data.locationOrder);
        }
      }
    };
    fetchSettings();
  }, [gatheringID]);

  const uniqueLocations = Array.from(
    new Set(schedule.map((event) => event.location)),
  );

  const uniqueEventTypes = Array.from(
    new Set(schedule.map((event) => event.eventType).filter((type) => type)), // Filter to exclude empty strings
  );

  const toggleForm = () => {
    setIsFormExpanded(!isFormExpanded); // Toggle the form expansion state
  };

  /**
   * handleDownloadCsvTemplate
   * Creates a CSV file with the required headers
   * and triggers a download. Name is dynamic based on gatheringName.
   */
  const handleDownloadCsvTemplate = () => {
    const sanitizedGatheringName = gatheringName
      ? gatheringName.toLowerCase().replace(/\s+/g, "_")
      : "gathering";

    const csvContent = [
      "EventName,Description,StartDate,StartTime,EndTime,Location",
      '"Opening Ceremony","Short description, 500 characters max","2026-05-15","9:00 AM","10:00 AM","Main Stage"',
      '"Lunch Break","Optional description","05/15/2026","12:00 PM","1:00 PM","Dining Hall"',
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.setAttribute(
      "download",
      `itiniri_${sanitizedGatheringName}_schedule_template.csv`,
    );

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    URL.revokeObjectURL(url);
  };

  /**
   * handleFileUploadClick
   * Triggers the hidden file input when the user clicks the "Upload CSV" button.
   */
  const handleFileUploadClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = ""; // Reset file input if needed
      fileInputRef.current.click();
    }
  };

  const convertRowsToCsv = (rows) => {
    return rows
      .map((row) =>
        row
          .map((cell) => {
            const safeCell = String(cell || "").replace(/"/g, '""');
            return `"${safeCell}"`;
          })
          .join(","),
      )
      .join("\n");
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setIsLoading(true);
    setCsvImportErrors("");
    setParsedEvents([]);
    setImportResults(null);
    setFailedEvents([]);
    setEditingFailedEvent(null);

    try {
      const extension = file.name.split(".").pop()?.toLowerCase();
      let fileText = "";

      if (extension === "csv") {
        fileText = await file.text();
      } else if (extension === "xls" || extension === "xlsx") {
        const data = await file.arrayBuffer();
        const workbook = XLSX.read(data, { type: "array" });
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        const rows = XLSX.utils.sheet_to_json(worksheet, { header: 1, raw: false });

        if (!rows || rows.length === 0) {
          throw new Error("Spreadsheet appears to be empty.");
        }

        fileText = convertRowsToCsv(rows);
      } else {
        throw new Error("Please upload a CSV or Excel file (.csv, .xls, .xlsx).");
      }

      const validatedEvents = validateCsvData(fileText);

      if (validatedEvents.length === 0) {
        throw new Error("No valid events were found in the upload.");
      }

      setParsedEvents(validatedEvents);
      setIsImportPreviewOpen(true);
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      const message = error?.message || "Unable to parse the file.";
      setCsvImportErrors(message);
      alert(
        `Upload failed:\n\n${message}\n\nPlease check the file and try again.`,
      );
    }
  };

  const handleConfirmBulkImport = async () => {
    if (!parsedEvents.length) return;

    setIsLoading(true);
    const results = {
      total: parsedEvents.length,
      imported: 0,
      skipped: 0,
      errors: [],
    };

    for (const eventObj of parsedEvents) {
      try {
        const id = await handleAddEventToSchedule(eventObj);
        if (id) {
          results.imported += 1;
        } else {
          results.skipped += 1;
          results.errors.push({
            event: eventObj,
            message: "Already exists or failed validation.",
          });
        }
      } catch (error) {
        results.skipped += 1;
        results.errors.push({
          event: eventObj,
          message: error?.message || "Failed to import this event.",
        });
      }
    }

    setImportResults(results);
    setParsedEvents([]);
    setIsImportPreviewOpen(false);
    setIsLoading(false);
  };

  const handleCancelBulkImport = () => {
    setParsedEvents([]);
    setCsvImportErrors("");
    setIsImportPreviewOpen(false);
  };

  const handleDownloadCurrentSchedule = () => {
    if (!schedule || schedule.length === 0) return;

    const csvRows = [
      ["EventName", "Description", "StartDate", "StartTime", "EndTime", "Location"],
      ...schedule.map((event) => [
        event.eventName || "",
        event.description || "",
        event.startDate || "",
        event.startTime || "",
        event.endTime || "",
        event.location || "",
      ]),
    ];

    const csvText = convertRowsToCsv(csvRows);
    const blob = new Blob([csvText], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `itiniri_${gatheringName || "schedule"}_export.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleOpenSpreadsheet = () => {
    setIsSpreadsheetOpen(true);
  };

  const handleSaveFromSpreadsheet = async (changes) => {
    if (!Array.isArray(changes)) return { persistedRows: [], errors: [] };
    const results = { persistedRows: [], errors: [] };

    for (const change of changes) {
      const { rowIndex, event } = change;
      try {
        let persistedEvent;

        if (event.id) {
          await ModifyEvent(event.id, event);
          persistedEvent = event;

          if (typeof saveEventDetails === "function") {
            saveEventDetails(persistedEvent);
          }
        } else {
          const id = await handleAddEventToSchedule(event);
          if (!id) {
            throw new Error(
              "This row could not be created. It may duplicate an existing event.",
            );
          }
          persistedEvent = { ...event, id };
        }

        results.persistedRows.push({ rowIndex, event: persistedEvent });
      } catch (err) {
        results.errors.push({
          rowIndex,
          event,
          message: err?.message || String(err),
        });
      }
    }

    if (results.errors.length) {
      console.error("Spreadsheet save errors:", results.errors);
    }

    return results;
  };

  return (
    <div className="input-container">
      <h3 className="tab-title">Event Schedule</h3>
      <p>
        Enter the Events in your Schedule using the Add A New Event button. Or
        if you prefer, download our spreadsheet template, fill it out with your
        schedule details, and then upload your schedule all at once. Once you
        have added Events, your Schedule will appear at the bottom of the page,
        where you can edit or delete Events.
      </p>
      <div className="csv-format-help">
        <p>
          CSV uploads require these columns in this order:
          <strong>
            {" "}
            EventName, Description, StartDate, StartTime, EndTime, Location
          </strong>
          .
        </p>
        <p>
          Accepted date formats: <strong>YYYY-MM-DD</strong> or{" "}
          <strong>MM/DD/YYYY</strong>. Accepted time formats:{" "}
          <strong>9:00 AM</strong>, <strong>09:00</strong>, or{" "}
          <strong>14:30</strong>.
        </p>
        <p>
          Event names should be 80 characters or fewer. Descriptions should be
          500 characters or fewer. Locations should be 120 characters or fewer.
          If a field contains a comma, wrap it in quotes.
        </p>
      </div>
      <div className="csv-button-container">
        <button className="csv-button" onClick={handleDownloadCsvTemplate}>
          Download CSV Template
        </button>
        <button className="csv-button" onClick={handleFileUploadClick}>
          Upload CSV / Excel
        </button>
        <button className="csv-button" onClick={handleOpenSpreadsheet}>
          Edit in Spreadsheet
        </button>
        {schedule.length > 0 && (
          <button className="csv-button" onClick={handleDownloadCurrentSchedule}>
            Download Current Schedule
          </button>
        )}
      </div>
      {csvImportErrors && (
        <div className="csv-import-error-banner">
          <strong>Upload error:</strong> {csvImportErrors}
        </div>
      )}
      {importResults && (
        <div className="csv-import-summary">
          <strong>Bulk import results:</strong>
          <p>
            {importResults.imported} imported, {importResults.skipped} skipped,
            {importResults.errors.length} issues.
          </p>
          {importResults.errors.length > 0 && (
            <p className="error-message">
              Some rows were skipped. Please review the CSV and try again.
            </p>
          )}
        </div>
      )}
      {isImportPreviewOpen && (
        <Modal
          isOpen={isImportPreviewOpen}
          onClose={handleCancelBulkImport}
          style={{ width: "45%" }}
        >
          <div className="import-preview-modal">
            <h3>Review imported events</h3>
            <p>
              We parsed {parsedEvents.length} rows. Confirm these events to
              add them to your schedule.
            </p>
            <div className="preview-event-list">
              {parsedEvents.slice(0, 6).map((event, idx) => (
                <div key={idx} className="preview-event-item">
                  <div>
                    <strong>{event.EventName}</strong>
                    <span>{event.Location}</span>
                  </div>
                  <div>
                    {event.StartDate} • {event.StartTime} – {event.EndTime}
                  </div>
                </div>
              ))}
              {parsedEvents.length > 6 && (
                <div className="preview-event-note">
                  Showing first 6 rows. {parsedEvents.length - 6} more rows will
                  also be imported.
                </div>
              )}
            </div>
            <div className="modal-actions">
              <button className="csv-button" onClick={handleConfirmBulkImport}>
                Confirm Import
              </button>
              <button className="cancel-button" onClick={handleCancelBulkImport}>
                Cancel
              </button>
            </div>
          </div>
        </Modal>
      )}
      {/* Hidden File Input */}
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: "none" }}
        accept=".csv,.xls,.xlsx"
        onChange={handleFileChange}
      />
      {/* Toggle to reveal or hide the Event creation form */}
      <div className="event-creation-toggle" onClick={toggleForm}>
        {isFormExpanded ? "Hide Event Creation Form" : "Add a New Event"}
      </div>
      {isFormExpanded && (
        <EventCreationForm
          handleAddEventToSchedule={handleAddEventToSchedule}
          locations={uniqueLocations}
          eventTypes={uniqueEventTypes}
          startDate={startDate}
          endDate={endDate}
        />
      )}
      {schedule.length > 0 && (
        <ScheduleList
          schedule={schedule}
          handleDeleteEvent={handleDeleteEvent}
          hostID={hostID}
          saveEventDetails={saveEventDetails}
          startDate={startDate} // <-- add this
          endDate={endDate} // <-- add this
        />
      )}
      {isLoading && (
        <Modal isOpen={isLoading} style={{ width: "30%" }}>
          <div style={{ padding: 30, textAlign: "center" }}>
            <h4>Processing your CSV...</h4>
            <p>Please wait while we validate and save your events.</p>
          </div>
        </Modal>
      )}
      <SpreadsheetEditor
        isOpen={isSpreadsheetOpen}
        onClose={() => setIsSpreadsheetOpen(false)}
        rows={schedule}
        gatheringID={gatheringID}
        onSaveRows={handleSaveFromSpreadsheet}
      />
      {failedEvents.length > 0 && (
        <Modal
          isOpen={true}
          onClose={() => setFailedEvents([])}
          style={{ width: "40%" }}
        >
          <h4>Some events could not be imported:</h4>
          <ul>
            {failedEvents.map((fail, idx) => (
              <li key={idx} style={{ marginBottom: 16 }}>
                <div>
                  <b>{fail.event.eventName || "Unnamed Event"}</b>
                  <ul>
                    {fail.errors.map((err, i) => (
                      <li key={i} style={{ color: "red" }}>
                        {err}
                      </li>
                    ))}
                  </ul>
                  <button
                    onClick={() => setEditingFailedEvent(fail.event)}
                    style={{ marginTop: 8 }}
                  >
                    Fix & Save
                  </button>
                </div>
              </li>
            ))}
          </ul>
          <button onClick={() => setFailedEvents([])}>Close</button>
        </Modal>
      )}
      {editingFailedEvent && (
        <Modal
          isOpen={true}
          onClose={() => setEditingFailedEvent(null)}
          style={{ width: "40%" }}
        >
          <EventCreationForm
            handleAddEventToSchedule={async (fixedEvent) => {
              await handleAddEventToSchedule(fixedEvent);
              setEditingFailedEvent(null);
              // Optionally remove from failedEvents
              setFailedEvents(
                failedEvents.filter((f) => f.event !== editingFailedEvent),
              );
            }}
            locations={uniqueLocations}
            eventTypes={uniqueEventTypes}
            startDate={startDate}
            endDate={endDate}
            initialEvent={editingFailedEvent}
          />
        </Modal>
      )}
    </div>
  );
};

export default ScheduleTab;
